import { API, ROLES, SCOPES } from '@/config';
import type { AccountResponse } from '@/features/auth/api';

const moderationBasePath = `${API.wikiBase}/moderation`;
const moderationQueuePath = `${API.wikiBase}/moderation/proposals`;
const moderatorRoles = new Set([ROLES.moderator, ROLES.superadmin]);

export function hasSuperadminRole(account?: AccountResponse | null): boolean {
  if (!account) {
    return false;
  }

  return account.role_id.trim().toLocaleLowerCase('en-US') === ROLES.superadmin;
}

export function hasModeratorScope(account?: AccountResponse | null): boolean {
  if (!account) {
    return false;
  }

  const roleID = account.role_id.trim().toLocaleLowerCase('en-US');
  if (moderatorRoles.has(roleID)) {
    return true;
  }

  return (account.extra_scopes ?? []).some(scopeGrantsModeration);
}

function scopeGrantsModeration(rawScope: string): boolean {
  const scope = rawScope.trim();

  if (scope === '*') {
    return true;
  }

  const [method, ownership, path] = scope.split(':', 3);
  if (!method || !ownership || !path) {
    return false;
  }

  if (method !== 'write' && method !== '*') {
    return false;
  }

  if (ownership !== SCOPES.adminOwnership && ownership !== '*') {
    return false;
  }

  return pathMatches(path, moderationBasePath) || pathMatches(path, moderationQueuePath);
}

function pathMatches(scopePath: string, requestPath: string): boolean {
  if (scopePath === '*' || scopePath === requestPath) {
    return true;
  }

  const wildcardIndex = scopePath.indexOf('*');
  if (wildcardIndex < 0) {
    return false;
  }

  return requestPath.startsWith(scopePath.slice(0, wildcardIndex));
}
